import { cn } from '@/app/lib/utils';
import CustomSelect, { Option } from '../ui/CustomSelect';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Avatar from '../ui/Avatar';
import { getContacts, getContact } from '@/api/contacts';
import Icon from '../ui/Icon';
import { useParams } from 'next/navigation';

type Props = {
  defaultValue: any;
  label?: string;
  required?: boolean;
  wrapperClassName?: string;
  className?: string;
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  placeholder?: string;
  fetchSelectedOptionOnMount?: boolean;
  multiple?: boolean;
  onOptionSelect: (option: any) => void;
  filterParams?: Record<string, string>;
};

const ContactSelect = ({
  defaultValue,
  label,
  required,
  wrapperClassName,
  className,
  searchable,
  clearable,
  disabled,
  placeholder,
  onOptionSelect,
  fetchSelectedOptionOnMount,
  multiple,
  filterParams,
}: Props) => {
  const [search, setSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [isOptionCbTriggered, setIsOptionCbTriggered] = useState(false);
  const { clientId } = useParams<{ clientId: string }>();
  const { data: options, isLoading } = useQuery({
    queryKey: ['contacts', clientId, search, filterParams],
    queryFn: () => {
      const fParams = new URLSearchParams();
      if (filterParams) {
        Object.keys(filterParams).forEach(key => {
          fParams.set(key, filterParams[key]);
        });
      }

      return getContacts({
        clientId: clientId || '',
        search: search,
        filterSearchParams: fParams,
      });
    },
    enabled: !!clientId,
  });
  const {
    mutate: getContactMutation,
    data: missingContact,
    isPending: isLoadingMissingContact,
  } = useMutation({
    mutationFn: (id: string) => getContact(clientId || '', id),
    onError: error => {
      console.error('error', error);
    },
  });

  const optionsList = useMemo(() => {
    const list: Option[] =
      options?.data?.map(option => {
        const name = [option.contact_info?.name?.first, option.contact_info?.name?.last]
          .filter(Boolean)
          .join(' ');
        return {
          id: option.id,
          value: name || '-',
          prefix: (
            <div className="mr-2">
              <Avatar
                size={32}
                name={name || '-'}
                src={option.contact_info?.photo_url}
                isExternalUrl
                initialsClassName="text-xs"
              />
            </div>
          ),
        };
      }) || [];
    if (!multiple && selectedOption && list.find(o => o.id === selectedOption.id) === undefined) {
      list.unshift({
        id: selectedOption.id.toString(),
        value: selectedOption.value.toString(),
        prefix: selectedOption.prefix,
      });
    }
    if (multiple && selectedOptions) {
      // find missing options
      const missingOptions = selectedOptions.filter(o => !list.some(c => c.id === o.id));
      list.unshift(...missingOptions);
    }
    if (missingContact) {
      list.unshift({
        id: missingContact.id,
        value:
          (missingContact.contact_info?.name?.first || '') +
            ' ' +
            (missingContact.contact_info?.name?.last || '') || '-',
        prefix: (
          <div className="mr-2">
            <Avatar
              size={32}
              name={
                (missingContact.contact_info?.name?.first || '') +
                ' ' +
                (missingContact.contact_info?.name?.last || '')
              }
              src={missingContact.contact_info?.photo_url}
              isExternalUrl
              initialsClassName="text-xs"
            />
          </div>
        ),
      });
    }
    return list;
  }, [options, selectedOption, selectedOptions, multiple, missingContact]);

  useEffect(() => {
    if (fetchSelectedOptionOnMount && defaultValue && !isOptionCbTriggered && !isLoading) {
      let values: Option | Option[] | null = null;
      if (multiple) {
        values = optionsList.filter(o => defaultValue.includes(o.id)) || [];
      } else {
        values = optionsList.find(o => o.id === defaultValue) || null;
      }
      if ((Array.isArray(values) && values.length !== 0) || values !== null) {
        onOptionSelect(values);
        setIsOptionCbTriggered(true);
      } else if (defaultValue || (Array.isArray(defaultValue) && defaultValue.length !== 0)) {
        // call api
        getContactMutation(defaultValue);
      }
    }
  }, [
    fetchSelectedOptionOnMount,
    defaultValue,
    optionsList,
    onOptionSelect,
    isOptionCbTriggered,
    isLoading,
    multiple,
    getContactMutation,
  ]);

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-sm font-medium mb-1.5 flex items-center gap-1 text-muted-foreground">
          <span>
            {label}
            {required && <span className="text-red-600 ml-1">*</span>}
          </span>
          {isLoadingMissingContact && (
            <Icon
              name="loader-2-fill"
              size={4}
              aria-label="Loading"
              className="animate-spin w-4 h-4"
            />
          )}
        </label>
      )}
      <CustomSelect
        options={optionsList || []}
        placeholder={placeholder}
        defaultValue={defaultValue}
        onOptionSelect={option => {
          console.log('option', option);
          if (multiple) {
            setSelectedOptions(option as Option[]);
          } else {
            setSelectedOption(option as Option);
          }
          onOptionSelect(option as Option);
        }}
        searchable={searchable}
        clearable={clearable}
        disabled={disabled}
        triggerClassNames={className}
        onSearch={search => {
          setSearch(search);
        }}
        searchLoading={isLoading}
        multiple={multiple}
      />
    </div>
  );
};

export default ContactSelect;
